import React from "react"
import { observer } from "mobx-react-lite"
import moment from "moment"
import { useAlert } from "react-alert"
import { useNavigate } from "react-router-dom"

import Text from "@components/ui/Typography/Text"
import useModal from "@components/modals/useModal"
import { ModalsTypes } from "@components/modals/constants"
import { useController, useStore } from "@store"
import useActiveAvatar from "@pages/search/ActiveAvatarContext/useActiveAvatar"
import List from "@components/ui/List/List"
import Icon from "@components/ui/Icon/Icon"
import Button from "@components/ui/Button/Button"
import { QueryHistoryData } from "@framework/types/analytics"
import { countSuffix } from "@utils/numberUtils"
import { copyToClipboard } from "@utils/clipboard"
import mainRoutes from "@root/main.routes"
import Loader from "@components/ui/Loader/BarLoader"
import { useSolution } from "@pages/launch-solutions/ActiveSolutionContext"
import { reportURLToId } from "@utils/workflow-report"

import styles from "./QuestionCard.module.sass"

export type QuestionCardProps = {
  question: QueryHistoryData
  onExpertsAdded: () => void
  onClose?: () => void
}

const QuestionCard: React.FC<QuestionCardProps> = observer(
  ({ question, onExpertsAdded, onClose }) => {
    const alert = useAlert()
    const navigate = useNavigate()

    const modal = useModal(ModalsTypes.CONFIRM_MODAL)

    const { showModal: showDirectAssignModal } = useModal(
      ModalsTypes.ASSIGN_EXPERTS_TO_QUESTIONS_MODAL
    )

    const { solution } = useSolution()
    const { avatar } = useActiveAvatar()

    const {
      solutionsStore,
      factFinderSolutionStore: { searchFlowStore },
      contentManagerStore: { companies },
      knowledgeStore: { assignQuestionToExpertDirectly },
      restrictionsStore: access,
    } = useStore()

    const { factFinderSolutionController } = useController()

    const [copyLoading, setCopyLoading] = React.useState(false)

    const askRecentQuestion = (historyItem: QueryHistoryData) => {
      solutionsStore.showRecentActivity(false)
      searchFlowStore.reset()
      factFinderSolutionController.clearHighLights()
      if (solution?.id) {
        factFinderSolutionController.restoreRecentQuestion(
          historyItem,
          avatar,
          solution.id,
          solutionsStore.appliedFilters,
          companies
        )
      }
    }
    const handleClick = (historyItem: QueryHistoryData) => {
      if (!searchFlowStore?.flowSequence?.length) askRecentQuestion(historyItem)
      else
        modal.showModal({
          message: (
            <>
              Are you sure you want to continue?
              <br /> This will replace the current results with the saved answer.
            </>
          ),
          confirm: "positive",
          onConfirm: () => askRecentQuestion(historyItem),
        })
    }

    const handleCopyLink = async () => {
      setCopyLoading(true)
      const result = await assignQuestionToExpertDirectly(
        question.question,
        [],
        undefined,
        "",
        question.summary ?? undefined,
        avatar?.id
      )
      if (result.status === "FAILED") {
        alert.error(result.message)
        return false
      }
      const questionId = result.data[0].id
      if (!questionId) {
        alert.error("Failed to generate link")
        return false
      }
      copyToClipboard(
        `${window.location.origin}${mainRoutes.question(questionId)}`
      )
      alert.success(`Question link copied to clipboard`)
      setCopyLoading(false)
      return true
    }

    const handleImprovementClick = async () => {
      if (access.canSuggestImprovements) {
        navigate(mainRoutes.question(question.expertQuestionId))
      }
    }

    const handleSubmit = async (expertIds: string[], questionNote: string) => {
      const result = await assignQuestionToExpertDirectly(
        question.question,
        expertIds,
        undefined,
        questionNote,
        question.summary ?? undefined,
        avatar?.id
      )
      if (result.status === "FAILED") {
        alert.error(result.message)
        return false
      }
      const totalExperts = expertIds.length
      alert.success(
        `Question successfully assigned to expert${countSuffix(totalExperts)}`
      )
      onExpertsAdded()
      return true
    }

    const handleReminderClick = () => {
      showDirectAssignModal({
        title: "Post to Expert",
        questions: [question.question],
        expertQuestionId: question.expertQuestionId,
        postedExperts:
          question.experts?.map((item) => ({
            ...item,
            isExpertVerified: !!question.expertAnswers?.some(
              (e) => e.id === item.id
            ),
          })) || [],
        onSubmit: handleSubmit,
        onCopy: handleCopyLink,
      })
    }

    const { totalAnswered, expertQuestionId } = question

    const isExpertAssigned = !!expertQuestionId
    const isExpertVerified = isExpertAssigned && parseInt(totalAnswered, 10) > 0
    const statusText = isExpertVerified
      ? "Expert Verified"
      : isExpertAssigned
      ? "Pending"
      : ""

    const isSearchQuery = question.recordType === "question"

    const reportId = question.reportUrl
      ? reportURLToId(question.reportUrl)
      : null

    return (
      <div className={styles.root}>
        {isExpertAssigned && access.canViewQuestions && (
          <List overflow="hidden" direction="row" gutter="4">
            <Icon
              color={isExpertVerified ? "green" : "gold"}
              name={isExpertVerified ? "shield-check-fill" : "timer-fill"}
            />
            <Text variant="caption2">{statusText}</Text>
          </List>
        )}

        <Text
          tabIndex={0}
          onKeyPress={() => {}}
          role="button"
          key={question.id}
          onClick={
            isSearchQuery ? () => handleClick(question) : undefined
          }
          color="text100Color"
          variant="caption1"
          className={styles.question}
        >
          Q:{" "}
          {isSearchQuery
            ? question.question || "[No question]"
            : question.documentType}
        </Text>

        <div className={styles.footer}>
          <Text
            className={styles.timestamp}
            variant="caption2"
            color="text50Color"
          >
            {moment(question.createdAt).fromNow()}
          </Text>
          {isSearchQuery ? (
            isExpertAssigned &&
            !isExpertVerified &&
            access.canViewQuestions && (
              <div className={styles.footerButtons}>
                {access.canPostToExpert && (
                  <>
                    <Button
                      after={copyLoading && <Loader />}
                      disabled={copyLoading}
                      onClick={handleCopyLink}
                      size="tiny"
                      color="secondary"
                      before={<Icon name="link" />}
                    >
                      Copy Link
                    </Button>

                    <Button
                      size="tiny"
                      color="primary"
                      onClick={handleReminderClick}
                    >
                      Send Reminder
                    </Button>
                  </>
                )}
                {access.canSuggestImprovements && (
                  <Button
                    size="tiny"
                    color="primary"
                    onClick={handleImprovementClick}
                  >
                    Improve This Answer
                  </Button>
                )}
              </div>
            )
          ) : (
            <div className={styles.footerButtons}>
              <Button
                after={copyLoading && <Loader />}
                disabled={copyLoading}
                onClick={handleCopyLink}
                size="tiny"
                color="secondary"
                before={<Icon name="link" />}
              >
                Copy Link
              </Button>

              {reportId != null && (
                <Button
                  size="tiny"
                  color="default"
                  variant="outlined"
                  before={<Icon name="external-link" />}
                  onClick={() => {
                    onClose?.()
                    setTimeout(() => {
                      searchFlowStore.showReport(reportId)
                    }, 300)
                  }}
                >
                  Open Report
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)

export default QuestionCard
